import { Effect } from 'effect';

export type SupportedLocale = 'en' | 'zh-CN';

export const LOCALE_STORAGE_KEY = 'okra-locale';
export const SUPPORTED_LOCALES: readonly SupportedLocale[] = ['en', 'zh-CN'];

export interface LocaleSignals {
  stored?: string | null;
  edgeHint?: string | null;
  /** Browser language is deliberately ignored for automatic localization. */
  browserLanguages?: readonly string[];
}

export function isLocalizedPublicPathEffect(pathname: string): Effect.Effect<boolean> {
  return Effect.sync(
    () => pathname === '/tools' || pathname === '/tools/' || pathname.startsWith('/tools/'),
  );
}

export function isLocalizedPublicPath(pathname: string): boolean {
  return Effect.runSync(isLocalizedPublicPathEffect(pathname));
}

export function normalizeLocaleEffect(value?: string | null): Effect.Effect<SupportedLocale | null> {
  return Effect.sync(() => {
    if (!value) return null;
    const normalized = value.trim().toLowerCase();
    if (normalized === 'en' || normalized.startsWith('en-')) return 'en';
    if (normalized === 'zh-cn' || normalized === 'zh-hans' || normalized.startsWith('zh-hans-')) {
      return 'zh-CN';
    }
    return null;
  });
}

export function normalizeLocale(value?: string | null): SupportedLocale | null {
  return Effect.runSync(normalizeLocaleEffect(value));
}

export const resolveInitialLocaleEffect = Effect.fn('web.i18n.resolveInitial')(function* ({
  stored,
  edgeHint,
}: LocaleSignals) {
  return (
    (yield* normalizeLocaleEffect(stored)) ??
    (yield* normalizeLocaleEffect(edgeHint)) ??
    'en'
  );
});

export function resolveInitialLocale(signals: LocaleSignals): SupportedLocale {
  return Effect.runSync(resolveInitialLocaleEffect(signals));
}
